import { AxiosResponse } from 'axios';
import { HightouchOrg, Source } from 'modules/dataWarehouse/types';
import { ApiMethod, getRaw } from 'services/api';
import {
  RestQueryHookOptions,
  UseRestMutationOptions,
  useRestMutation,
  useRestQuery,
} from 'utils/hooks/api';

export const CREATE_SOURCE_URL = '/integrations/hightouch/sources';
export const SYNC_URL = '/integrations/hightouch/syncs';
export const PROVISION_WORKSPACE_URL = '/integrations/hightouch/provisionWorkspace';
export const ALLOWED_SOURCE_TYPES_URL = '/integrations/hightouch/allowedSources';

/** Creates a source in the Hightouch workspace of this project */
export const useCreateSource = (options?: UseRestMutationOptions) =>
  useRestMutation(CREATE_SOURCE_URL, ApiMethod.post, options);

/** Creates a sync in the Hightouch workspace of this project */
export const useCreateSync = (options?: UseRestMutationOptions) =>
  useRestMutation(SYNC_URL, ApiMethod.post, options);

/** Fetches all syncs from the Hightouch workspace of this project */
export const useGetSyncs = (options?: RestQueryHookOptions) =>
  useRestQuery<{ data: Source[] }>(SYNC_URL, {}, options);

/** Configurable destination tab when accessing a single sync */
export enum GetSyncConfig {
  /** Default and Runs are the same screen today, but eventually Default will be more like an overview page */
  Default = 'view-sync',
  Configuration = 'view-sync-configuration',
  Model = 'view-sync-model',
  Runs = 'view-sync-runs',
  Schedule = 'view-sync-schedule',
}

type GetSyncUrl = {
  config: GetSyncConfig;
  onError?: (reason: any) => void;
  onSettled?: () => void;
  onSuccess?: (value: AxiosResponse<{ url: string }, any>) => void | PromiseLike<void>;
  syncId: number;
};

/** Get URL for a specific sync that can be loaded into an iframe or new window */
export const getSyncUrl = ({ syncId, config, onSuccess, onError, onSettled }: GetSyncUrl) => {
  return getRaw<{ url: string }>(`${SYNC_URL}/${syncId}`, { params: { config } })
    .then(onSuccess)
    .catch(onError)
    .finally(onSettled);
};

/** Ensures that the current project has a Hightouch workspace. */
export const useProvisionWorkspace = (options?: UseRestMutationOptions) =>
  useRestMutation(PROVISION_WORKSPACE_URL, ApiMethod.post, options);

/** Fetches a URL to view/edit source information from the Hightouch workspace of this project */
export const useGetSource = (sourceId: number, options?: RestQueryHookOptions) =>
  useRestQuery<{ url: string }>(`/integrations/hightouch/sources/${sourceId}`, {}, options);

/** Get allowed source types */
export const useGetAllowedSourceTypes = (options?: RestQueryHookOptions) =>
  useRestQuery<{ allowedSourceTypes: string[] }>(ALLOWED_SOURCE_TYPES_URL, {}, options);

/** Hightouch organization and billing details */
export const HIGHTOUCH_BILLING_URL = '/integrations/hightouch/billing';

export const useHightouchOrganization = (options?: RestQueryHookOptions) =>
  useRestQuery<HightouchOrg>(HIGHTOUCH_BILLING_URL, {}, options);
